/**
 * The admin panel — every studio, its status, and how much it has used the
 * product. Nothing else: no student names, no measurements, no photos. See
 * apps/api/src/repositories/platform.ts for why that line is drawn here.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateLong } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { Field } from '../components/ui/Field.js';
import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../components/ui/ToastContext.js';
import './PlatformDashboard.css';

interface StudioSummary {
  id: string;
  name: string;
  studioCode: string;
  status: 'active' | 'suspended' | 'closed';
  createdAt: string;
  studentCount: number;
  reportCount: number;
  lastActivityAt: string | null;
}

interface IssuedCredentials {
  name: string;
  studioCode: string;
  ownerUsername: string;
  password: string;
}

interface R2OrphanCheck {
  totalObjects: number;
  referencedCount: number;
  orphanCount: number;
  orphanBytes: number;
}

/**
 * A byte count for one operational read-out, not a product figure — the
 * @pp/shared formatters exist to keep a report's preview and PDF in step
 * (Doc 6), which is not what this number is for.
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_LABEL: Record<StudioSummary['status'], string> = {
  active: 'Aktif',
  suspended: 'Askıda',
  closed: 'Kapalı',
};

export function PlatformDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, error, loading, reload } = useApiData<{ items: StudioSummary[] }>(
    '/api/platform/studios',
  );
  const [adminName, setAdminName] = useState<string>();

  useEffect(() => {
    api
      .get<{ name: string }>('/api/platform/session')
      .then((session) => setAdminName(session.name))
      .catch(() => navigate('/yonetim/giris', { replace: true }));
  }, [navigate]);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [studioCode, setStudioCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);

  const [pendingSuspend, setPendingSuspend] = useState<StudioSummary | null>(null);
  const [pendingReset, setPendingReset] = useState<StudioSummary | null>(null);

  const [r2Check, setR2Check] = useState<R2OrphanCheck | null>(null);
  const [r2Busy, setR2Busy] = useState(false);
  const [r2Error, setR2Error] = useState<string>();

  const items = data?.items ?? [];

  async function createStudio(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(undefined);
    setFieldErrors({});

    try {
      const created = await api.post<IssuedCredentials & { id: string }>(
        '/api/platform/studios',
        { name, studioCode },
      );
      setIssued(created);
      setAdding(false);
      setName('');
      setStudioCode('');
      reload();
    } catch (err) {
      if (err instanceof ApiError) {
        const fields = err.details?.fields;
        if (Array.isArray(fields)) {
          setFieldErrors(
            Object.fromEntries(
              fields.map((field) => [
                String(field),
                field === 'studioCode'
                  ? 'Bu salon kodu zaten kullanılıyor. Başka bir tane deneyin.'
                  : 'Bu alanı kontrol edin.',
              ]),
            ),
          );
        }
        setFormError(messageFor(err.code));
      } else setFormError(messageFor('INTERNAL_ERROR'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus(studio: StudioSummary) {
    setPendingSuspend(null);
    const suspending = studio.status === 'active';
    try {
      await api.post(
        `/api/platform/studios/${studio.id}/${suspending ? 'suspend' : 'reactivate'}`,
      );
      toast.show(suspending ? 'Stüdyo askıya alındı.' : 'Stüdyo yeniden aktif edildi.');
      reload();
    } catch (err) {
      toast.show(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  async function resetPassword(studio: StudioSummary) {
    setPendingReset(null);
    try {
      const result = await api.post<{ password: string }>(
        `/api/platform/studios/${studio.id}/reset-password`,
      );
      setIssued({
        name: studio.name,
        studioCode: studio.studioCode,
        ownerUsername: 'yonetici',
        password: result.password,
      });
    } catch (err) {
      toast.show(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  async function checkStorage() {
    setR2Busy(true);
    setR2Error(undefined);
    try {
      setR2Check(await api.get<R2OrphanCheck>('/api/platform/r2-orphans'));
    } catch (err) {
      setR2Error(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setR2Busy(false);
    }
  }

  async function logout() {
    await api.post('/api/platform/logout').catch(() => {});
    navigate('/yonetim/giris', { replace: true });
  }

  return (
    <section className="platform">
      <header className="platform__head">
        <div>
          <h1 className="page__title">Yönetim Paneli</h1>
          {adminName && <p className="platform__welcome">Hoş geldin, {adminName}</p>}
        </div>
        <div className="platform__head-actions">
          {!adding && (
            <Button type="button" onClick={() => setAdding(true)}>
              Yeni Stüdyo
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => void logout()}>
            Çıkış Yap
          </Button>
        </div>
      </header>

      {(error || formError) && <p role="alert">{error ?? formError}</p>}

      {/* Shown once. There is no way to read it back — only to reset it. */}
      {issued && (
        <div className="platform__issued" role="status">
          <p>
            <b>{issued.name}</b> için giriş bilgileri:
          </p>
          <dl className="platform__issued-fields">
            <dt>Salon kodu</dt>
            <dd>{issued.studioCode}</dd>
            <dt>Kullanıcı adı</dt>
            <dd>{issued.ownerUsername}</dd>
            <dt>Şifre</dt>
            <dd>
              <code>{issued.password}</code>
            </dd>
          </dl>
          <p className="platform__issued-note">
            Bu bilgileri stüdyoya siz iletin. Şifre bir daha görüntülenemez — yalnızca
            sıfırlanabilir.
          </p>
          <Button type="button" variant="secondary" onClick={() => setIssued(null)}>
            Anladım
          </Button>
        </div>
      )}

      {adding && (
        <form onSubmit={createStudio} className="platform__form" noValidate>
          <Field
            label="Salon Adı"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
            required
          />
          <Field
            label="Salon Kodu"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            hint="Girişte kullanılacak. Türkçe harfler sadeleştirilir."
            value={studioCode}
            onChange={(e) => setStudioCode(e.target.value)}
            error={fieldErrors.studioCode}
            required
          />
          <div className="platform__form-actions">
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={busy}
              disabled={name.trim() === '' || studioCode.trim().length < 2}
            >
              Oluştur
            </Button>
          </div>
        </form>
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && (
        <div className="profile__empty">
          <p>Henüz stüdyo yok.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="platform__table-wrap">
          <table className="platform__table">
            <thead>
              <tr>
                <th scope="col">Salon</th>
                <th scope="col">Kod</th>
                <th scope="col">Durum</th>
                <th scope="col">Öğrenci</th>
                <th scope="col">Rapor</th>
                <th scope="col">Son Aktivite</th>
                <th scope="col">Oluşturuldu</th>
                <th scope="col">
                  <span className="pp-visually-hidden">İşlemler</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((studio) => (
                <tr key={studio.id}>
                  <td>{studio.name}</td>
                  <td>{studio.studioCode}</td>
                  <td>
                    <span
                      className={`platform__status platform__status--${studio.status}`}
                    >
                      {STATUS_LABEL[studio.status]}
                    </span>
                  </td>
                  <td>{studio.studentCount}</td>
                  <td>{studio.reportCount}</td>
                  <td>
                    {studio.lastActivityAt
                      ? formatDateLong(studio.lastActivityAt.slice(0, 10))
                      : '—'}
                  </td>
                  <td>{formatDateLong(studio.createdAt.slice(0, 10))}</td>
                  <td>
                    <div className="platform__row-actions">
                      <button
                        type="button"
                        className="platform__link"
                        onClick={() => setPendingReset(studio)}
                      >
                        Şifre Sıfırla
                      </button>
                      <button
                        type="button"
                        className={
                          studio.status === 'active'
                            ? 'platform__link platform__link--warn'
                            : 'platform__link'
                        }
                        onClick={() =>
                          studio.status === 'active'
                            ? setPendingSuspend(studio)
                            : void toggleStatus(studio)
                        }
                      >
                        {studio.status === 'active' ? 'Askıya Al' : 'Aktif Et'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --------------------------------------------------------- storage */}
      <div className="platform__storage">
        <h2 className="platform__storage-title">Depo Kontrolü</h2>
        <p className="platform__storage-note">
          Yüklenirken yarıda kesilen bir fotoğraf ya da logo, hiçbir öğrenciye veya rapora
          bağlı olmadan depoda kalabilir. Bu kontrol böyle dosyaları bulur — hiçbir şeyi
          silmez.
        </p>
        <Button
          type="button"
          variant="secondary"
          loading={r2Busy}
          onClick={() => void checkStorage()}
        >
          Depoyu Kontrol Et
        </Button>
        {r2Error && <p role="alert">{r2Error}</p>}
        {r2Check && (
          <p className="platform__storage-result">
            {r2Check.orphanCount === 0
              ? `Depoda ${r2Check.totalObjects} dosya var, hepsi bir kayıtla eşleşiyor.`
              : `Depodaki ${r2Check.totalObjects} dosyadan ${r2Check.orphanCount} tanesi (${formatBytes(r2Check.orphanBytes)}) hiçbir kayıtla eşleşmiyor.`}
          </p>
        )}
      </div>

      {pendingSuspend && (
        <ConfirmDialog
          title={`${pendingSuspend.name} askıya alınsın mı?`}
          body="Stüdyo giriş yapamaz hale gelir. Verileri silinmez, dilediğiniz zaman yeniden aktif edebilirsiniz."
          confirmLabel="Askıya Al"
          destructive
          onCancel={() => setPendingSuspend(null)}
          onConfirm={() => void toggleStatus(pendingSuspend)}
        />
      )}

      {pendingReset && (
        <ConfirmDialog
          title={`${pendingReset.name} için şifre sıfırlansın mı?`}
          body="Yeni bir şifre üretilir ve size bir kez gösterilir. Stüdyonun açık oturumları kapanır."
          confirmLabel="Sıfırla"
          onCancel={() => setPendingReset(null)}
          onConfirm={() => void resetPassword(pendingReset)}
        />
      )}
    </section>
  );
}
